/**
 * Turns a refused role assignment into words for shop staff.
 *
 * The role pickers lock roles you can't give up front, so these show only
 * when the screen and the server disagree (an older screen, or your own
 * access changed while the screen was open). Used wherever a role is given:
 * a user's roles, Add / Invite User, a location group, the new store wizard.
 *
 *   403 ROLE_NOT_ASSIGNABLE       { details: { role_id, reason, keys } }
 *   403 INSUFFICIENT_PERMISSIONS  no right to give that kind of role there
 *   400 ACCOUNT_ROLE_AT_STORE / STORE_NOT_IN_ACCOUNT / ASSIGNEE_NOT_IN_ACCOUNT
 */
import { HttpError } from '@turn-connect/shared';

export const ROLE_NOT_ASSIGNABLE = 'ROLE_NOT_ASSIGNABLE';

const FIXED: Record<string, string> = {
  ACCOUNT_ROLE_AT_STORE: 'An account role covers every store, so it can’t be given at just one store.',
  STORE_NOT_IN_ACCOUNT: 'That store isn’t part of this account.',
  ASSIGNEE_NOT_IN_ACCOUNT: 'This person isn’t a member of this account, so they can’t be given its roles.',
};

interface RefusalBody {
  code?: unknown;
  error?: unknown;
  details?: { reason?: unknown };
}

/** An HttpError, or a plain `{ status, body, message }` from a client that doesn't throw. */
type Refusal = HttpError | { status?: number; body?: unknown; message?: string } | unknown;

function parts(err: Refusal): { body?: RefusalBody; message?: string } {
  if (err instanceof HttpError)
    return { body: (err.body && typeof err.body === 'object' ? err.body : undefined) as RefusalBody, message: err.message };
  if (err && typeof err === 'object') {
    const e = err as { body?: unknown; message?: unknown };
    return {
      body: (e.body && typeof e.body === 'object' ? e.body : undefined) as RefusalBody,
      message: typeof e.message === 'string' ? e.message : undefined,
    };
  }
  return {};
}

/** The API's error code, if the refusal has one. */
export function roleAssignErrorCode(err: Refusal): string | undefined {
  const code = parts(err).body?.code;
  return typeof code === 'string' ? code : undefined;
}

/** True when the API refused to give a role (any of the codes above). */
export function isRoleAssignRefusal(err: Refusal): boolean {
  const code = roleAssignErrorCode(err);
  return code === ROLE_NOT_ASSIGNABLE || code === 'INSUFFICIENT_PERMISSIONS' || (!!code && code in FIXED);
}

const withHelp = (text: string, help: string) => `${text.trim().replace(/[.]?$/, '.')} ${help}`;

/**
 * The message for a refused role assignment. The server's own sentence is
 * already plain ("You can't give this role because it includes permissions
 * you don't have: Manage policies"), so it is kept, with who to ask added.
 */
export function roleAssignErrorText(err: Refusal, fallback = 'The role could not be given.'): string {
  const { body, message } = parts(err);
  const code = typeof body?.code === 'string' ? body.code : undefined;
  const server = typeof body?.error === 'string' && body.error ? body.error : message;
  if (code === ROLE_NOT_ASSIGNABLE) {
    const staff = body?.details?.reason === 'staff_only';
    const text =
      server || (staff ? 'Only TURN-CONNECT staff can give this role.' : 'You can’t give this role: it has permissions you don’t have.');
    return withHelp(text, staff ? 'Ask TURN-CONNECT support.' : 'Ask an account administrator.');
  }
  if (code === 'INSUFFICIENT_PERMISSIONS') {
    const text = server && /can.t give/i.test(server) ? server : 'You can’t give roles there.';
    return withHelp(text, 'Ask an account administrator.');
  }
  if (code && FIXED[code]) return FIXED[code];
  return server || fallback;
}
